import React, { useState } from "react";
import dayjs, { Dayjs } from "dayjs";
import cn from "classnames";
import { LeftOutlined, RightOutlined } from "@ant-design/icons";
import BasicButton from "../../BasicButton";
import useIsPhone from "../../hooks/useIsPhone";
import styles from "./styles.module.scss";

export type RangeValue = [Dayjs | null, Dayjs | null];

interface CalendarCell {
  date: Dayjs;
  adjacent: boolean;
}

interface RangeCalendarProps {
  value: RangeValue;
  onChange: (value: RangeValue) => void;
  onApply: () => void;
  onClear: () => void;
}

const WEEK_DAYS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

// Weeks start on Monday; days of the neighbouring months fill the first and
// last rows greyed out and can't be picked.
const monthCells = (month: Dayjs): CalendarCell[] => {
  const start = month.startOf("month");
  const lead = (start.day() + 6) % 7;
  const cells: CalendarCell[] = [];
  for (let i = lead; i > 0; i--) {
    cells.push({ date: start.subtract(i, "day"), adjacent: true });
  }
  for (let i = 0; i < month.daysInMonth(); i++) {
    cells.push({ date: start.add(i, "day"), adjacent: false });
  }
  const end = start.endOf("month");
  for (let i = 1; cells.length % 7 !== 0; i++) {
    cells.push({ date: end.add(i, "day"), adjacent: true });
  }
  return cells;
};

const selectedDaysText = ([start, end]: RangeValue) => {
  if (!start || !end) return "0 days";
  const total = end.diff(start, "day") + 1;
  const months = Math.floor(total / 30);
  const days = total % 30;
  if (months > 0) return `${months} month ${days} days`;
  return `${total} days`;
};

/**
 * The client's two-month range calendar (Balance date filters, Figma
 * 563:12380): brand endpoints, a light band between them, a footer with the
 * length of the range and Clear / Apply.
 */
const RangeCalendar: React.FC<RangeCalendarProps> = ({
  value,
  onChange,
  onApply,
  onClear,
}) => {
  // A phone has room for one month, with both arrows on it.
  const isPhone = useIsPhone();
  // Filters look back: open on the range, or on last month and this one
  // (this one alone on a phone).
  const [left, setLeft] = useState(() =>
    (value[0] || dayjs().subtract(isPhone ? 0 : 1, "month")).startOf("month"),
  );
  const right = left.add(1, "month");
  const [start, end] = value;

  const pick = (date: Dayjs) => {
    if (!start || end) onChange([date, null]);
    else if (date.isBefore(start, "day")) onChange([date, start]);
    else onChange([start, date]);
  };

  const isEndpoint = (date: Dayjs) =>
    (!!start && date.isSame(start, "day")) ||
    (!!end && date.isSame(end, "day"));
  const isInside = (date: Dayjs) =>
    !!start && !!end && date.isAfter(start, "day") && date.isBefore(end, "day");
  const inRange = (cell?: CalendarCell) =>
    !!cell && !cell.adjacent && (isEndpoint(cell.date) || isInside(cell.date));

  const dayClass = (cells: CalendarCell[], index: number) => {
    const { date, adjacent } = cells[index];
    if (adjacent) return cn(styles.dayCell, styles.muted);
    const complete = !!start && !!end && !start.isSame(end, "day");
    return cn(styles.dayCell, styles.day, {
      [styles.selected]: isEndpoint(date) && !complete,
      [styles.rangeStart]: complete && date.isSame(start, "day"),
      [styles.rangeEnd]: complete && date.isSame(end, "day"),
      [styles.inRange]: isInside(date),
      // soft ends of the band where it meets a row edge or a gap
      [styles.roundLeft]:
        isInside(date) && (index % 7 === 0 || !inRange(cells[index - 1])),
      [styles.roundRight]:
        isInside(date) && (index % 7 === 6 || !inRange(cells[index + 1])),
    });
  };

  const renderMonth = (month: Dayjs, nav: "prev" | "next" | "both") => {
    const cells = monthCells(month);
    return (
      <div className={styles.month}>
        <div className={styles.monthHeader}>
          {nav !== "next" && (
            <button
              type="button"
              className={styles.navButton}
              onClick={() => setLeft(left.subtract(1, "month"))}
              aria-label="Previous month"
            >
              <LeftOutlined />
            </button>
          )}
          <span className={styles.monthTitle}>{month.format("MMMM YYYY")}</span>
          {nav !== "prev" && (
            <button
              type="button"
              className={styles.navButton}
              onClick={() => setLeft(left.add(1, "month"))}
              aria-label="Next month"
            >
              <RightOutlined />
            </button>
          )}
        </div>
        <div className={styles.weekDays}>
          {WEEK_DAYS.map((day) => (
            <div key={day} className={styles.weekDay}>
              {day}
            </div>
          ))}
        </div>
        <div className={styles.days}>
          {cells.map((cell, index) => (
            <div
              key={cell.date.format("YYYY-MM-DD")}
              className={dayClass(cells, index)}
              onClick={() => !cell.adjacent && pick(cell.date)}
            >
              {cell.date.date()}
            </div>
          ))}
        </div>
      </div>
    );
  };

  return (
    <div className={styles.calendar}>
      <div className={styles.months}>
        {isPhone ? (
          renderMonth(left, "both")
        ) : (
          <>
            {renderMonth(left, "prev")}
            {renderMonth(right, "next")}
          </>
        )}
      </div>
      <div className={styles.calendarFooter}>
        <span className={styles.selectedDays}>{selectedDaysText(value)}</span>
        <div className={styles.calendarActions}>
          <BasicButton
            title="Clear"
            variant="secondary"
            onClick={onClear}
            sx={{ height: 32, minWidth: 80, fontSize: 14, lineHeight: "20px" }}
          />
          <BasicButton
            title="Apply"
            variant="primary"
            onClick={onApply}
            disabled={!start || !end}
            sx={{ height: 32, minWidth: 80, fontSize: 14, lineHeight: "20px" }}
          />
        </div>
      </div>
    </div>
  );
};

export default RangeCalendar;
